import React, { useState, useEffect } from 'react';
import { 
  FileText, Search, RefreshCw, CheckCircle, XCircle, 
  Clock, Users, MessageSquare, Filter, Download
} from 'lucide-react';
import { whatsappAPI } from '../../services/api';

/**
 * WhatsApp Broadcast Logs Page
 * View history of all sent broadcasts and individual message logs
 */
const WhatsAppLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [limit, setLimit] = useState(100);
  
  useEffect(() => {
    loadLogs();
  }, [limit]);
  
  const loadLogs = async () => {
    try {
      setLoading(true);
      const response = await whatsappAPI.getBroadcastLogs(limit);
      setLogs(response.data.data || []);
    } catch (error) {
      console.error('Error loading logs:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchQuery || 
      log.broadcastName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.recipientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      log.phone?.includes(searchQuery);
    const matchesStatus = statusFilter === 'all' || log.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  const stats = {
    total: logs.length,
    sent: logs.filter(l => l.status === 'sent').length,
    delivered: logs.filter(l => l.status === 'delivered').length,
    failed: logs.filter(l => l.status === 'failed').length
  };
  
  const exportLogs = () => {
    const csvContent = [
      ['Timestamp', 'Broadcast', 'Recipient', 'Phone', 'Type', 'Status', 'Error'].join(','),
      ...filteredLogs.map(log => [
        new Date(log.createdAt).toISOString(),
        `"${log.broadcastName || ''}"`,
        `"${log.recipientName || ''}"`,
        log.phone || '',
        log.recipientType || '',
        log.status || '',
        `"${log.error || ''}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `whatsapp_logs_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  
  return (
    <div className="space-y-6" data-testid="whatsapp-logs-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 rounded-xl shadow-sm border border-gray-200">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <FileText className="text-gray-600" size={24} />
            Broadcast Logs
          </h2>
          <p className="text-sm text-gray-500 mt-1">View history of all broadcast messages</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportLogs}
            className="flex items-center px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <Download size={18} className="mr-2" /> Export CSV
          </button>
          <button
            onClick={loadLogs}
            className="flex items-center px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            <RefreshCw size={18} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div 
          onClick={() => setStatusFilter('all')}
          className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
            statusFilter === 'all' ? 'border-gray-500 shadow-md' : 'border-transparent'
          }`}
        >
          <p className="text-sm text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div 
          onClick={() => setStatusFilter('sent')}
          className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
            statusFilter === 'sent' ? 'border-blue-500 shadow-md' : 'border-transparent'
          }`}
        >
          <p className="text-sm text-gray-500">Sent</p>
          <p className="text-2xl font-bold text-blue-600">{stats.sent}</p>
        </div>
        <div 
          onClick={() => setStatusFilter('delivered')}
          className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
            statusFilter === 'delivered' ? 'border-green-500 shadow-md' : 'border-transparent'
          }`}
        >
          <p className="text-sm text-gray-500">Delivered</p>
          <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
        </div>
        <div 
          onClick={() => setStatusFilter('failed')}
          className={`bg-white rounded-xl p-5 border-2 cursor-pointer transition-all hover:shadow-md ${
            statusFilter === 'failed' ? 'border-red-500 shadow-md' : 'border-transparent'
          }`}
        >
          <p className="text-sm text-gray-500">Failed</p>
          <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by broadcast name, recipient, or phone..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
            />
          </div>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none"
          >
            <option value={50}>Last 50</option>
            <option value={100}>Last 100</option>
            <option value={500}>Last 500</option>
            <option value={1000}>Last 1000</option>
          </select>
        </div>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      ) : filteredLogs.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Broadcast
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipient
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Error
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLogs.map(log => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {new Date(log.createdAt).toLocaleString('en-IN')}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {log.broadcastName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {log.recipientName || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                      {log.phone || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        log.recipientType === 'property'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {log.recipientType || 'unknown'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`flex items-center gap-1 text-sm ${
                        log.status === 'sent' || log.status === 'delivered'
                          ? 'text-green-600'
                          : log.status === 'failed'
                          ? 'text-red-600'
                          : 'text-gray-500'
                      }`}>
                        {log.status === 'sent' || log.status === 'delivered' ? (
                          <CheckCircle size={14} />
                        ) : log.status === 'failed' ? (
                          <XCircle size={14} />
                        ) : (
                          <Clock size={14} />
                        )}
                        {log.status || 'pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-red-500 max-w-xs truncate">
                      {log.error || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-500">
            Showing {filteredLogs.length} of {logs.length} logs
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No logs found</h3>
          <p className="text-gray-500">
            {searchQuery || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Broadcast logs will appear here after sending messages'
            }
          </p>
        </div>
      )}
    </div>
  );
};

export default WhatsAppLogs;
